import React, {
  Fragment,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  VisibilityOutlined,
  ChatBubbleOutlineOutlined,
  EditOutlined,
  RefreshOutlined,
  AccountTreeOutlined,
  PersonOutlineOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Typography,
  Badge,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Box,
  Chip,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import { format, isSameDay, parseISO } from "date-fns";
import { grey } from "@material-ui/core/colors";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { WhatsappsFilter } from "../../components/WhatsappsFilter";
import { UsersFilter } from "../../components/UsersFilter";
import { StatusFilter } from "../../components/StatusFilter";
import TicketConversationModal from "../../components/TicketConversationModal";
import ContactModal from "../../components/ContactModal";

// Por tag: agrupa tickets por contato e fica só com o último ticket (updatedAt mais recente) de cada contato
function useContactsWithLastTicket(data) {
  return useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((tag) => {
      const tickets = tag.tickets || [];
      const byContact = new Map();
      tickets.forEach((t) => {
        const cid = t.contactId || t.contact?.id;
        if (!cid) return;
        const existing = byContact.get(cid);
        if (!existing || new Date(t.updatedAt) > new Date(existing.updatedAt)) {
          byContact.set(cid, t);
        }
      });
      return {
        ...tag,
        contactsWithLastTicket: Array.from(byContact.values()),
        totalCount: tag.totalCount ?? tickets.length,
        hasMore: tag.hasMore ?? false,
      };
    });
  }, [data]);
}

const CARD_WIDTH = 320;
const CARD_MIN_HEIGHT = 340;
const CARD_RADIUS = 18;

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    display: "grid",
    gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_WIDTH}px, 1fr))`,
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    overflowX: "hidden",
    overflowY: "auto",
    ...theme.scrollbarStyles,
    maxHeight: "calc(100vh - 200px)",
  },
  column: {
    minHeight: CARD_MIN_HEIGHT,
    display: "flex",
    flexDirection: "column",
  },
  columnPaper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: CARD_MIN_HEIGHT,
    borderRadius: CARD_RADIUS,
    overflow: "hidden",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow:
      theme.palette.type === "dark" ? theme.shadows[8] : theme.shadows[2],
  },
  cardHeader: {
    padding: theme.spacing(1.5, 2),
    minHeight: 72,
    borderTopLeftRadius: CARD_RADIUS,
    borderTopRightRadius: CARD_RADIUS,
  },
  card: {
    flex: "0 1 auto",
    maxHeight: 300,
    minHeight: 120,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    borderTop:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.08)"
        : "1px solid rgba(0, 0, 0, 0.06)",
    borderBottomLeftRadius: CARD_RADIUS,
    borderBottomRightRadius: CARD_RADIUS,
    backgroundColor:
      theme.palette.type === "dark" ? theme.palette.grey[800] : "#fff",
  },
  listWrap: {
    padding: theme.spacing(0, 1),
  },
  contactRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  contactInfo: {
    flex: 1,
    minWidth: 0,
  },
  contactName: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  metaCaption: {
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
  },
  rowActionIcon: {
    color: theme.palette.text.secondary,
    cursor: "pointer",
  },
  statusBadge: {
    fontSize: "0.7rem",
    padding: "2px 6px",
    marginRight: 4,
  },
  connectionTag: {
    background: "green",
    color: "#FFF",
    marginRight: 1,
    padding: 1,
    fontWeight: "bold",
    borderRadius: 3,
    fontSize: "0.6em",
  },
  lastMessageTime: {
    textAlign: "right",
    marginRight: 4,
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
  },
  lastMessageTimeUnread: {
    textAlign: "right",
    color: theme.palette.success.main,
    fontWeight: "bold",
    marginRight: 4,
    fontSize: "0.75rem",
  },
  dashboardZoom: {
    transform: "scale(0.9)",
    transformOrigin: "top left",
    width: "111.12%",
    maxWidth: "111.12%",
  },
  filtersBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  filterField: {
    minWidth: 200,
  },
  dividerInset: {
    marginLeft: 48,
    backgroundColor: theme.palette.divider,
  },
}));

const DashboardTags = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const companyId = user?.companyId;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedWhatsappIds, setSelectedWhatsappIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [withUnreadOnly, setWithUnreadOnly] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [openPendingOnly, setOpenPendingOnly] = useState(true);
  const [allTags, setAllTags] = useState([]);
  const [popupTicket, setPopupTicket] = useState(null);
  const [contactToEditId, setContactToEditId] = useState(null);
  const [loadingMoreTagId, setLoadingMoreTagId] = useState(null);

  const socketRefetchTimeoutRef = useRef(null);
  const SOCKET_REFETCH_DEBOUNCE_MS = 5 * 60 * 1000; // 5 minutos quando disparado por socket

  const dataWithContacts = useContactsWithLastTicket(data);

  useEffect(() => {
    let isMounted = true;
    api.get("/tags/list", { params: { kanban: 0 } })
      .then(({ data }) => isMounted && setAllTags(Array.isArray(data) ? data : []))
      .catch((err) => toastError(err));
    return () => { isMounted = false; };
  }, []);

  const buildKanbanParams = useCallback(
    (extra = {}) => ({
      tagIds: selectedTagIds.length ? selectedTagIds.map((t) => t.id) : undefined,
      whatsappIds: selectedWhatsappIds.length ? selectedWhatsappIds.map((w) => w.id) : undefined,
      userIds: selectedUserIds.length ? selectedUserIds.map((u) => u.id) : undefined,
      statusFilters: selectedStatus.length ? selectedStatus.map((s) => s.status) : undefined,
      withUnreadMessages: withUnreadOnly ? "true" : undefined,
      showAll: user?.profile === "admin" && showAll ? "true" : undefined,
      openPendingOnly: openPendingOnly ? "true" : undefined,
      limitPerTag: 50,
      maxTicketsTotal: 3000,
      ...extra,
    }),
    [
      selectedTagIds,
      selectedWhatsappIds,
      selectedUserIds,
      selectedStatus,
      withUnreadOnly,
      showAll,
      openPendingOnly,
      user?.profile,
    ]
  );

  const fetchDashboard = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data: res } = await api.get("/dashboard/tags-kanban", {
        params: buildKanbanParams(),
      });
      setData(Array.isArray(res) ? res : []);
    } catch (err) {
      toastError(err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, buildKanbanParams]);

  /** Carrega próxima página de uma coluna (scroll). API: offsetPerTag = tickets já carregados da tag. */
  const fetchMoreForTag = useCallback(
    async (tag) => {
      if (!companyId || !tag?.id || !tag.hasMore || loadingMoreTagId === tag.id) return;
      const offset = Array.isArray(tag.tickets) ? tag.tickets.length : 0;
      setLoadingMoreTagId(tag.id);
      try {
        const { data: res } = await api.get("/dashboard/tags-kanban", {
          params: buildKanbanParams({
            tagIds: [tag.id],
            offsetPerTag: offset,
            limitPerTag: 50,
          }),
        });
        const row = Array.isArray(res) && res.length ? res[0] : null;
        if (!row || !Array.isArray(row.tickets)) {
          return;
        }
        setData((prev) =>
          prev.map((t) => {
            if (t.id !== tag.id) return t;
            const seen = new Set((t.tickets || []).map((x) => x.id));
            const extra = row.tickets.filter((x) => x && !seen.has(x.id));
            return {
              ...t,
              tickets: [...(t.tickets || []), ...extra],
              totalCount: row.totalCount != null ? row.totalCount : t.totalCount,
              hasMore: !!row.hasMore,
            };
          })
        );
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingMoreTagId(null);
      }
    },
    [companyId, buildKanbanParams, loadingMoreTagId]
  );

  const handleColumnScroll = useCallback(
    (e, tag) => {
      const el = e.currentTarget;
      if (!el || !tag?.hasMore || loadingMoreTagId === tag.id) return;
      const threshold = 100;
      if (el.scrollHeight - el.scrollTop - el.clientHeight > threshold) return;
      fetchMoreForTag(tag);
    },
    [fetchMoreForTag, loadingMoreTagId]
  );

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (!socket || !companyId) return;
    const refetch = () => fetchDashboard();
    const scheduleRefetch = () => {
      if (socketRefetchTimeoutRef.current) clearTimeout(socketRefetchTimeoutRef.current);
      socketRefetchTimeoutRef.current = setTimeout(() => {
        socketRefetchTimeoutRef.current = null;
        refetch();
      }, SOCKET_REFETCH_DEBOUNCE_MS);
    };
    const onTicket = (d) => {
      if (d.action === "create" || d.action === "update" || d.action === "delete") scheduleRefetch();
    };
    socket.on("connect", refetch);
    socket.on(`company-${companyId}-ticket`, onTicket);
    socket.on(`company-${companyId}-appMessage`, scheduleRefetch);
    return () => {
      if (socketRefetchTimeoutRef.current) clearTimeout(socketRefetchTimeoutRef.current);
      socket.off("connect", refetch);
      socket.off(`company-${companyId}-ticket`, onTicket);
      socket.off(`company-${companyId}-appMessage`, scheduleRefetch);
    };
  }, [socket, companyId, fetchDashboard]);

  const handleTagFilter = (tags) => setSelectedTagIds(Array.isArray(tags) ? tags : []);
  const handleWhatsappFilter = (items) => setSelectedWhatsappIds(Array.isArray(items) ? items : []);
  const handleUserFilter = (items) => setSelectedUserIds(Array.isArray(items) ? items : []);
  const handleStatusFilter = (items) => setSelectedStatus(Array.isArray(items) ? items : []);

  return (
    <MainHeader>
      <Box display="flex" flexDirection="column" width="100%">
        <MainHeaderButtonsWrapper>
          <Title>{i18n.t("dashboardTags.title") || "Dashboard de Tags"}</Title>
          <Tooltip title={i18n.t("dashboardTags.refresh") || "Atualizar"}>
            <span>
              <IconButton
                onClick={() => fetchDashboard()}
                disabled={loading}
                color="inherit"
                size="small"
                aria-label={i18n.t("dashboardTags.refresh") || "Atualizar"}
              >
                <RefreshOutlined />
              </IconButton>
            </span>
          </Tooltip>
        </MainHeaderButtonsWrapper>

        <div className={classes.dashboardZoom}>
        <div className={classes.filtersBar}>
          <Box minWidth={180} maxWidth={280} className={classes.filterField}>
            <Autocomplete
              multiple
              size="small"
              options={allTags}
              value={selectedTagIds}
              onChange={(e, v) => handleTagFilter(v)}
              getOptionLabel={(option) => option.name || ""}
              getOptionSelected={(a, b) => a?.id === b?.id}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    style={{
                      backgroundColor: option.color || "#eee",
                      color: "#fff",
                    }}
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} variant="outlined" placeholder="Tags" />
              )}
            />
          </Box>
          <Box className={classes.filterField}>
            <WhatsappsFilter onFiltered={handleWhatsappFilter} />
          </Box>
          <Box className={classes.filterField}>
            <UsersFilter onFiltered={handleUserFilter} />
          </Box>
          <Box className={classes.filterField}>
            <StatusFilter onFiltered={handleStatusFilter} />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={withUnreadOnly}
                onChange={(e) => setWithUnreadOnly(e.target.checked)}
                color="primary"
              />
            }
            label={i18n.t("dashboardTags.unreadOnly") || "Não lidos"}
          />
          {user?.profile === "admin" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                  color="primary"
                />
              }
              label={i18n.t("dashboardTags.showAll") || "Ver todos"}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={openPendingOnly}
                onChange={(e) => setOpenPendingOnly(e.target.checked)}
                color="primary"
              />
            }
            label={i18n.t("dashboardTags.onlyOpenPending") || "Apenas Ticket Aberto/Pendente"}
          />
        </div>

        <Paper className={classes.mainPaper} variant="outlined">
          {loading ? (
            <Typography color="textSecondary">
              {i18n.t("contacts.loading") || "Carregando..."}
            </Typography>
          ) : (
            <>
              {dataWithContacts.map((tag) => (
                <div key={tag.id} className={classes.column}>
                  <Paper elevation={2} className={classes.columnPaper}>
                    <CardHeader
                      className={classes.cardHeader}
                      style={{ backgroundColor: tag.color || "#DCDCDC" }}
                      title={
                        <span style={{ color: "#fff", fontWeight: "bold" }}>
                          {tag.name}
                        </span>
                      }
                      subheader={
                        <span style={{ color: "rgba(255,255,255,0.9)" }}>
                          {i18n.t("momentsUser.services") || "Atendimentos"}: {tag.contactsWithLastTicket?.length ?? 0}
                          {tag.totalCount != null &&
                          (tag.hasMore || (tag.tickets?.length ?? 0) < tag.totalCount)
                            ? ` (${i18n.t("dashboardTags.showingOf") || "de"} ${tag.totalCount})`
                            : ""}
                        </span>
                      }
                    />
                    <Paper
                      square
                      elevation={0}
                      className={classes.card}
                      onScroll={(e) => handleColumnScroll(e, tag)}
                    >
                      <List className={classes.listWrap} style={{ paddingTop: 0 }}>
                        {(tag.contactsWithLastTicket || []).map((ticket) => {
                          const contactName = ticket?.contact?.name?.length > 28
                            ? ticket.contact.name.substring(0, 25) + "..."
                            : (ticket.contact?.name || "-");
                          const statusLabel = ticket.status === "closed"
                            ? (i18n.t("tickets.search.filterConectionsOptions.closed") || "Fechado")
                            : ticket.status === "pending"
                              ? (i18n.t("tickets.search.filterConectionsOptions.pending") || "Pendente")
                              : (i18n.t("tickets.search.filterConectionsOptions.open") || "Aberto");
                          const canView = user?.profile === "admin" || ticket.userId === user?.id;
                          return (
                            <Fragment key={ticket.id}>
                              <ListItem dense className={classes.contactRow} disableGutters>
                                <ListItemAvatar style={{ minWidth: 40 }}>
                                  <Avatar
                                    alt={ticket.contact?.name}
                                    src={ticket.contact?.urlPicture}
                                    style={{ width: 32, height: 32 }}
                                  />
                                </ListItemAvatar>
                                <ListItemText
                                  className={classes.contactInfo}
                                  disableTypography
                                  primary={
                                    <Typography variant="body2" noWrap className={classes.contactName}>
                                      {contactName}
                                    </Typography>
                                  }
                                  secondary={
                                    <Fragment>
                                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4, marginBottom: 2 }}>
                                        <Chip
                                          size="small"
                                          label={statusLabel}
                                          className={classes.statusBadge}
                                          style={{
                                            backgroundColor: ticket.status === "closed" ? grey[500] : "#4caf50",
                                            color: "#fff",
                                          }}
                                        />
                                        <Typography
                                          component="span"
                                          variant="caption"
                                          className={
                                            Number(ticket.unreadMessages) > 0
                                              ? classes.lastMessageTimeUnread
                                              : classes.lastMessageTime
                                          }
                                        >
                                          {ticket.updatedAt
                                            ? isSameDay(parseISO(ticket.updatedAt), new Date())
                                              ? format(parseISO(ticket.updatedAt), "HH:mm")
                                              : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")
                                            : ""}
                                        </Typography>
                                      </div>
                                      {(ticket.queue || ticket.user) && (
                                        <div
                                          style={{
                                            display: "flex",
                                            flexWrap: "nowrap",
                                            gap: 6,
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {ticket.queue && (
                                            <Tooltip title={i18n.t("dashboardTags.queue") || "Fila"}>
                                              <Typography
                                                component="span"
                                                variant="caption"
                                                className={classes.metaCaption}
                                                style={{ flexShrink: 0 }}
                                              >
                                                <AccountTreeOutlined style={{ fontSize: 12 }} />
                                                <span style={{ color: ticket.queue.color || "inherit", fontWeight: 500 }}>
                                                  {ticket.queue.name}
                                                </span>
                                              </Typography>
                                            </Tooltip>
                                          )}
                                          {ticket.user && (
                                            <Tooltip title={i18n.t("dashboardTags.user") || "Usuário"}>
                                              <Typography
                                                component="span"
                                                variant="caption"
                                                className={classes.metaCaption}
                                              >
                                                <PersonOutlineOutlined style={{ fontSize: 12 }} />
                                                {ticket.user.name}
                                              </Typography>
                                            </Tooltip>
                                          )}
                                        </div>
                                      )}
                                    </Fragment>
                                  }
                                />
                                <Tooltip title={i18n.t("dashboardTags.editContact") || "Editar contato"}>
                                  <EditOutlined
                                    fontSize="small"
                                    className={classes.rowActionIcon}
                                    style={{ marginRight: canView ? 4 : 0 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (ticket?.contact?.id) setContactToEditId(ticket.contact.id);
                                    }}
                                  />
                                </Tooltip>
                                {canView && (
                                  <>
                                    <Tooltip title={i18n.t("dashboardTags.viewContent") || "Ver conteúdo"}>
                                      <ChatBubbleOutlineOutlined
                                        fontSize="small"
                                        className={classes.rowActionIcon}
                                        style={{ marginRight: 4 }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setPopupTicket(ticket);
                                        }}
                                      />
                                    </Tooltip>
                                    <Tooltip title={i18n.t("momentsUser.viewTicket") || "Acessar Ticket"}>
                                      <VisibilityOutlined
                                        fontSize="small"
                                        className={classes.rowActionIcon}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          history.push(`/tickets/${ticket.uuid}`);
                                        }}
                                      />
                                    </Tooltip>
                                  </>
                                )}
                              </ListItem>
                              <Divider variant="inset" component="li" className={classes.dividerInset} />
                            </Fragment>
                          );
                        })}
                        {loadingMoreTagId === tag.id && (
                          <Box display="flex" justifyContent="center" py={1}>
                            <CircularProgress size={22} />
                          </Box>
                        )}
                      </List>
                    </Paper>
                  </Paper>
                </div>
              ))}
            </>
          )}
        </Paper>
        </div>

        <TicketConversationModal
          open={!!popupTicket}
          onClose={() => setPopupTicket(null)}
          ticket={popupTicket}
        />
        <ContactModal
          open={!!contactToEditId}
          onClose={() => setContactToEditId(null)}
          contactId={contactToEditId}
        />
      </Box>
    </MainHeader>
  );
};

export default DashboardTags;
